import { Dispatch, SetStateAction, useEffect, useRef } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useDispatch } from 'react-redux'

import { Box, Flex, Spinner } from '@chakra-ui/react'

import { DarkProvider } from '@hivewatch/dark'

import { RefetchSOPFnType, SOPsTable } from '@/features/sop-tabs'
import { StandardOperatingProceduresQuery } from '@/graphql/generated/operations'
import { setSOPIncidentId } from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, VIEW, startTimer, trackEvent } from '@/utils/analytics'

import { SOPIncidentHeader } from './SOPIncidentHeader'

const HIDDEN_COLUMNS = ['type', 'facilities']
const HIDDEN_COLUMNS_REBRAND = ['type', 'facilities', 'actions']

export const SOPIncidentTable = ({
  facilityId,
  hideHeader = false,
  incidentId,
  sopData,
  isSopLoading,
  refetchSops,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  sortColumn,
  setSortColumn,
  sortOrder,
  setSortOrder,
}: {
  facilityId: string
  hideHeader?: boolean
  incidentId?: string
  sopData: StandardOperatingProceduresQuery | undefined
  isSopLoading: boolean
  refetchSops: RefetchSOPFnType
  pageIndex: number
  setPageIndex: Dispatch<SetStateAction<number>>
  pageSize: number
  setPageSize: Dispatch<SetStateAction<number>>
  sortColumn: string | null
  setSortColumn: Dispatch<SetStateAction<string | null>>
  sortOrder: 'asc' | 'desc' | null
  setSortOrder: Dispatch<SetStateAction<'asc' | 'desc' | null>>
}) => {
  const dispatch = useDispatch()
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const data = sopData
  const isLoading = isSopLoading
  const refetch = refetchSops

  const hasTrackedLoad = useRef(false)

  useEffect(() => {
    hasTrackedLoad.current = false
    startTimer(`${VIEW} SOPs Loaded`)
  }, [facilityId])

  useEffect(() => {
    if (data && !isLoading && !hasTrackedLoad.current) {
      hasTrackedLoad.current = true
      trackEvent(`${VIEW} SOPs Loaded`, {
        sop_count: data.standardOperatingProcedures.totalCount,
        facility_id: facilityId,
        incident_id: incidentId,
        entry_point: 'incident_drawer',
      })
    }
  }, [data, isLoading, facilityId, incidentId])

  const onItemClick = (id: string) => {
    trackEvent(`${BUTTON_PRESS} SOP Viewed`, {
      sop_id: id,
      facility_id: facilityId,
      incident_id: incidentId,
      entry_point: 'incident_drawer',
    })
    dispatch(setSOPIncidentId({ id }))
  }

  const sopsCount = data?.standardOperatingProcedures.totalCount

  return (
    <>
      {!hideHeader && (
        <SOPIncidentHeader
          facilityId={facilityId}
          incidentId={incidentId}
          sopsCount={sopsCount}
        />
      )}

      <Box
        h='calc(100vh - 24px)'
        m={isRebrand ? undefined : 'auto'}
        minWidth={isRebrand ? 0 : undefined}
        overflow='auto'
        p={isRebrand ? undefined : '24px'}
        pb={isRebrand ? '24px' : undefined}
        pt={isRebrand ? '0' : undefined}
        px={isRebrand ? '24px' : undefined}
        width={isRebrand ? '100%' : undefined}
      >
        {isLoading && !data ? (
          <Flex justifyContent='center' mt='10'>
            <Spinner
              emptyColor='gray.300'
              size='lg'
              speed='0.65s'
              thickness='3px'
            />
          </Flex>
        ) : (
          <DarkProvider>
            <SOPsTable
              hiddenColumns={
                isRebrand ? HIDDEN_COLUMNS_REBRAND : HIDDEN_COLUMNS
              }
              isLoading={isLoading && !data}
              onRowClick={onItemClick}
              pageIndex={pageIndex}
              pageSize={pageSize}
              refetch={refetch}
              setPageIndex={setPageIndex}
              setPageSize={setPageSize}
              setSortColumn={setSortColumn}
              setSortOrder={setSortOrder}
              shouldHideToolbar
              sops={data}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
              useCompactLayout={isRebrand}
              useMonoDate={isRebrand}
            />
          </DarkProvider>
        )}
      </Box>
    </>
  )
}
